import React from 'react';
import { Box, Button, TableCell, TableSortLabel, Typography } from '@mui/material';
import { NEUTRAL, SELECTED_EDGE, VIOLET } from '../styles/brandTokens';
import { ticketAnalysisTableSurfaceStyles } from './ticketAnalysisTableStyles';

export const NUMERIC_COL_WIDTH = 128;

const formatPct = (value: number) => `${value.toFixed(1)}%`;

export const headCellSx = {
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
  color: NEUTRAL.textSecondary,
  bgcolor: NEUTRAL.subtle,
  borderColor: NEUTRAL.border,
  whiteSpace: 'nowrap',
  py: 1,
};

export const tableSx = {
  ...ticketAnalysisTableSurfaceStyles,
  overflowX: 'auto',
  '& td': { verticalAlign: 'top', borderColor: NEUTRAL.border, py: 1.75 },
  '& tbody tr:last-of-type td': { borderBottom: 0 },
  '& tbody tr': { transition: 'background-color 150ms ease' },
  '& tbody tr:hover, & tbody tr:has(:focus-visible)': { bgcolor: NEUTRAL.hover },
  '& tbody tr.Mui-selected, & tbody tr.Mui-selected:hover': { bgcolor: VIOLET.wash, boxShadow: SELECTED_EDGE },
  '@media (prefers-reduced-motion: reduce)': { '& tbody tr': { transition: 'none' } },
};

export function SortableHead<K extends string>({ label, sortKey, sort, onSort, align = 'right', disabled = false, rowSpan, children }: {
  label: string; sortKey: K; sort: {key:K;direction:'asc'|'desc'}; onSort: (key: K) => void;
  align?: 'left' | 'right'; disabled?: boolean; rowSpan?: number; children?: React.ReactNode;
}) {
  const active = sort.key === sortKey;
  return (
    <TableCell align={align} rowSpan={rowSpan} sx={{ ...headCellSx, verticalAlign: 'bottom' }} sortDirection={active ? sort.direction : false}>
      <TableSortLabel active={active} direction={active ? sort.direction : sortKey === 'label' ? 'asc' : 'desc'}
        disabled={disabled} onClick={() => onSort(sortKey)} aria-label={`Sort by ${label}`}
        sx={{ flexDirection: align === 'right' ? 'row-reverse' : 'row', '&.Mui-active': { color: NEUTRAL.text } }}>
        {children ?? label}
      </TableSortLabel>
    </TableCell>
  );
}

/** Count over percent, both right-aligned with tabular figures so digits stack cleanly. */
export function MetricButton({ count, pct, ariaLabel, disabled, onClick, bar }: {
  count: number; pct: number; ariaLabel: string; disabled: boolean; onClick: () => void; bar?: boolean;
}) {
  const empty = count === 0;
  return (
    <Button aria-label={ariaLabel} disabled={disabled} onClick={onClick}
      sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%', minWidth: 0, px: 1, py: 0.5, ml: 'auto',
        textTransform: 'none', fontVariantNumeric: 'tabular-nums', lineHeight: 1.3, '&.Mui-disabled': { color: 'inherit' } }}>
      <Typography component="span" sx={{ fontSize: '0.9375rem', fontWeight: 600, color: empty ? 'text.disabled' : VIOLET.main }}>{count}</Typography>
      <Typography component="span" sx={{ fontSize: '0.75rem', color: empty ? 'text.disabled' : NEUTRAL.textSecondary }}>
        {empty ? '—' : formatPct(pct)}
      </Typography>
      {bar && <Box aria-hidden sx={{ mt: 0.75, width: 64, height: 3, borderRadius: 2, bgcolor: VIOLET.tint, overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.min(pct, 100)}%`, height: '100%', bgcolor: VIOLET.main }} />
      </Box>}
    </Button>
  );
}

